import { deepSignal } from 'deepsignal';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Description from '@components/overlays/shared/description.jsx';
import { formatColorName } from '@util/common.js';
import ColorPicker from '@components/pickers/color-picker/color.picker.jsx';
import Picker from '@components/input/picker.input.jsx';
import LinkSetup from '@components/overlays/shared/link-setup.jsx';
import { CHARITY } from '@util/const.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import {
  MilestoneOverlay,
  MilestoneOverlayBackgroundColors,
  toMilestonesOverlayBackgroundColor,
} from '@playlive/overlay-core/overlays/milestone';
import { Color } from '@playlive/overlay-core/colors';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';
import useCommon from '@hooks/useCommon.js';
import useDemoProgressBarData from '@hooks/useDemoProgressBarData.js';
import { useCallback, useEffect, useMemo } from 'preact/hooks';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  timer: '0',
  bgColor: Color.DEFAULT_RED,
  visual: 'rounded',
});

export default function MilestoneOverlayPage() {
  const { overlayInfo, overlayURL, setConfigState } = useCommon({
    config,
    forceTiltify: true,
  });

  const { demoMilestones } = useDemoProgressBarData(config);

  const filterColors = useCallback((color) => {
    return MilestoneOverlayBackgroundColors.find((c) => c === color);
  }, []);

  useEffect(() => {
    if (![...MilestoneOverlayBackgroundColors].includes(config.bgColor)) {
      setConfigState({ target: { name: 'bgColor', value: 'default-red' } });
    }
  }, [config.bgColor]);

  const timerOptions = useMemo(
    () => [
      {
        value: '5',
        name: 'Five minutes',
      },
      {
        value: '10',
        name: 'Ten minutes',
      },
      {
        value: '15',
        name: 'Fifteen minutes',
      },
      {
        value: '0',
        name: 'Always',
      },
    ],
    []
  );

  const themeOptions = useMemo(
    () => [
      {
        name: 'Slanted',
        value: 'slanted',
      },
      {
        name: 'Rounded',
        value: 'rounded',
      },
    ],
    []
  );

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper allowScaling={false}>
            <MilestoneOverlay
              currentAmount={350}
              goalAmount={1000}
              theme={config.visual}
              displayTimer={'0'}
              milestones={demoMilestones}
              backgroundColor={toMilestonesOverlayBackgroundColor(
                config.bgColor
              )}
              type={config.visual}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper>
          <CustomizeSection title="Choose Colors">
            <span className="label-text mb-2">
              Background color: <b>{formatColorName(config.bgColor)}</b>
            </span>

            <ColorPicker
              fieldName="bgColor"
              currentColor={config.bgColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />
          </CustomizeSection>

          <CustomizeSection title="Theme">
            <div className="mt-5">
              <Picker
                items={themeOptions}
                fieldName="visual"
                onChange={setConfigState}
                currentValue={config.visual}
                labelText=""
              />
            </div>
          </CustomizeSection>

          <CustomizeSection title="Misc.">
            <div className="mt-5">
              <Picker
                items={timerOptions}
                fieldName="timer"
                onChange={setConfigState}
                currentValue={config.timer}
                labelText="Display Timer"
              />
              <article className="prose text-sm mt-2 ml-1">
                Set amount of time to rotate future milestones visibility in and
                out on on the overlay
              </article>
            </div>
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <Description>
        <p className="pb-4">
          Enhance your Tiltify fundraising campaign with our dynamic Milestone
          overlay. Clearly display your next and future milestones on your
          stream. Seamlessly integrate into any streaming layout, show active
          milestones, choose to display all or rotate them in and out, and
          highlight completed milestones for optimal viewer engagement.
        </p>
      </Description>

      <LinkSetup
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
      />
    </div>
  );
}
